import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['trigger', 'panel'];
  static values = {
    placement: { type: String, default: 'top' }, // 'top' | 'bottom' | 'left' | 'right'
    trigger: { type: String, default: 'hover' }, // 'hover' | 'click'
    maxWidth: Number, // px
    gap: { type: Number, default: 8 }, // px
  };

  connect() {
    this.isOpen = false;
    this._outsideBound = false;
    this._leaveT = null;

    // Portal
    this.portalRoot = this._ensurePortalRoot();

    this.panelEl = this.hasPanelTarget
      ? this.panelTarget
      : this.element.querySelector('[data-tooltip-target="panel"]');
    if (!this.panelEl) {
      console.warn('[tooltip] Missing panel element');
      return;
    }
    this.panelOriginParent = this.panelEl.parentElement;

    if (this.triggerValue === 'click') {
      this._onTriggerClick = (e) => {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      };
      this.triggerTarget.addEventListener('click', this._onTriggerClick);
    } else {
      this._hoverIn = () => {
        clearTimeout(this._leaveT);
        this.open();
      };
      this._hoverOut = () => {
        clearTimeout(this._leaveT);
        this._leaveT = setTimeout(() => this.close(), 100);
      };

      this.triggerTarget.addEventListener('mouseenter', this._hoverIn);
      this.triggerTarget.addEventListener('mouseleave', this._hoverOut);
      this.panelEl.addEventListener('mouseenter', this._hoverIn);
      this.panelEl.addEventListener('mouseleave', this._hoverOut);

      this._onFocus = () => this.open();
      this._onBlur = () => this.close();
      this.triggerTarget.addEventListener('focus', this._onFocus);
      this.triggerTarget.addEventListener('blur', this._onBlur);
    }
  }

  disconnect() {
    this._teardownOutside();
    this._detachObservers();

    // Clean up any pending close transition listener
    if (this._closeTransitionHandler && this.panelEl) {
      this.panelEl.removeEventListener('transitionend', this._closeTransitionHandler);
      this._closeTransitionHandler = null;
    }

    if (this.panelEl && this.panelEl.parentElement === this.portalRoot) {
      this.panelOriginParent.appendChild(this.panelEl);
    }

    // Unbind
    if (this.triggerValue === 'click') {
      this.triggerTarget.removeEventListener('click', this._onTriggerClick);
    } else {
      this.triggerTarget.removeEventListener('mouseenter', this._hoverIn);
      this.triggerTarget.removeEventListener('mouseleave', this._hoverOut);
      this.panelEl.removeEventListener('mouseenter', this._hoverIn);
      this.panelEl.removeEventListener('mouseleave', this._hoverOut);
      this.triggerTarget.removeEventListener('focus', this._onFocus);
      this.triggerTarget.removeEventListener('blur', this._onBlur);
    }
  }

  // === API ===
  open() {
    if (this.isOpen || !this.panelEl) return;

    // Cancel any pending close transition listener
    if (this._closeTransitionHandler) {
      this.panelEl.removeEventListener('transitionend', this._closeTransitionHandler);
      this._closeTransitionHandler = null;
    }

    this.portalRoot.appendChild(this.panelEl);

    this.panelEl.classList.remove('hidden');
    this.panelEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    this._position();

    void this.panelEl.offsetHeight;

    this.panelEl.classList.remove('ss-transition-none', 'invisible');
    requestAnimationFrame(() => {
      this.panelEl.classList.remove('opacity-0', 'scale-95');
      this.panelEl.classList.add('opacity-100', 'scale-100');
    });

    this._setupOutside();
    this._attachObservers();

    this.isOpen = true;
  }

  close() {
    if (!this.isOpen || !this.panelEl) return;

    this.panelEl.classList.add('opacity-0', 'scale-95');
    this.panelEl.classList.remove('opacity-100', 'scale-100');

    // Store reference so it can be canceled if open() is called before transition ends
    this._closeTransitionHandler = () => {
      this.panelEl.classList.add('hidden');
      this.panelEl.removeEventListener('transitionend', this._closeTransitionHandler);
      this._closeTransitionHandler = null;
    };
    this.panelEl.addEventListener('transitionend', this._closeTransitionHandler, { once: true });

    this._teardownOutside();
    this._detachObservers();

    this.isOpen = false;
  }

  _position() {
    const panel = this.panelEl;
    const rect = this.triggerTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = this.gapValue || 8;
    const margin = 8;

    panel.classList.add('tt-no-max');
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : 300;
    const naturalW = Math.min(maxW, Math.max(panel.scrollWidth, 0) || maxW);
    const naturalH = Math.max(panel.scrollHeight || 0, 0);
    panel.classList.remove('tt-no-max');

    const pref = (this.placementValue || 'top').toLowerCase();

    const compute = (p) => {
      switch (p) {
        case 'bottom': {
          const top = rect.bottom + GAP;
          const left = Math.min(
            Math.max(rect.left + rect.width / 2 - naturalW / 2, margin),
            vw - naturalW - margin,
          );
          return { top, left, origin: 'top', cls: 'tt--bottom' };
        }
        case 'left': {
          const top = Math.min(
            Math.max(rect.top + rect.height / 2 - naturalH / 2, margin),
            vh - naturalH - margin,
          );
          const left = Math.max(rect.left - GAP - naturalW, margin);
          return { top, left, origin: 'right', cls: 'tt--left' };
        }
        case 'right': {
          const top = Math.min(
            Math.max(rect.top + rect.height / 2 - naturalH / 2, margin),
            vh - naturalH - margin,
          );
          const left = Math.min(rect.right + GAP, vw - naturalW - margin);
          return { top, left, origin: 'left', cls: 'tt--right' };
        }
        case 'top':
        default: {
          const top = Math.max(rect.top - GAP - naturalH, margin);
          const left = Math.min(
            Math.max(rect.left + rect.width / 2 - naturalW / 2, margin),
            vw - naturalW - margin,
          );
          return { top, left, origin: 'bottom', cls: 'tt--top' };
        }
      }
    };

    const order =
      pref === 'bottom'
        ? ['bottom', 'top', 'right', 'left']
        : pref === 'left'
          ? ['left', 'right', 'top', 'bottom']
          : pref === 'right'
            ? ['right', 'left', 'top', 'bottom']
            : ['top', 'bottom', 'right', 'left'];

    let best = compute(order[0]);
    const fits = (p) =>
      p.left >= margin &&
      p.left + naturalW <= vw - margin &&
      p.top >= margin &&
      p.top + naturalH <= vh - margin;

    if (!fits(best)) {
      for (let i = 1; i < order.length; i++) {
        const cand = compute(order[i]);
        if (fits(cand)) {
          best = cand;
          break;
        }
      }
    }

    // CSS variables (to avoid inline styles)
    panel.style.setProperty('--tt-left', `${Math.round(best.left)}px`);
    panel.style.setProperty('--tt-top', `${Math.round(best.top)}px`);
    panel.style.setProperty('--tt-max-w', `${Math.round(maxW)}px`);
    panel.style.setProperty('--tt-origin', best.origin);
  }

  // === Outside / ESC ===
  _setupOutside() {
    if (this._outsideBound) return;
    this._onDocPointerDown = (e) => {
      const t = e.target;
      const insidePanel = this.panelEl.contains(t);
      const insideAnchor = this.element.contains(t);
      if (!insidePanel && !insideAnchor) this.close();
    };
    this._onDocKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };

    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this._outsideBound = true;
  }
  _teardownOutside() {
    if (!this._outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this._outsideBound = false;
  }

  // === Relayout ===
  _attachObservers() {
    this._reposition = () => {
      if (this.isOpen) this._position();
    };
    window.addEventListener('scroll', this._reposition, true);
    window.addEventListener('resize', this._reposition);

    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.triggerTarget);

    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition };
      window.visualViewport.addEventListener('resize', this.vvo.onResize);
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll);
    }
  }
  _detachObservers() {
    window.removeEventListener('scroll', this._reposition, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize);
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll);
      this.vvo = undefined;
    }
  }

  // === Portal ===
  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      document.body.appendChild(root);
    }
    return root;
  }
}
